import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getCriancaAtiva, listarAlimentos } from "../../db/queries";
import { dataCurta } from "../../lib/format";
import { emojiAlimento } from "../../lib/emoji";
import { getNotasDaCrianca, listarProfissionais, marcarOrientacaoFeita } from "../queries";
import { EspecialidadeBadge } from "../ui/EspecialidadeBadge";

/**
 * P0.4b — "Pra fazer em casa": as orientações de TODAS as disciplinas consolidadas
 * numa lista só, do mais recente pro mais antigo. É a tela do CUIDADOR — ele lê o que
 * aplicar em casa sem caçar nota por nota. A família deixa de ser mensageira.
 */
export function OrientacoesCasa() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const profissionais = useLiveQuery(() => listarProfissionais(true)) ?? [];
  const alimentos = useLiveQuery(() => listarAlimentos(true)) ?? [];
  const notas = useLiveQuery(
    () => (crianca ? getNotasDaCrianca(crianca.id) : Promise.resolve([])),
    [crianca?.id],
  ) ?? [];

  // só as notas que têm orientação pra casa, mais recentes primeiro
  const comOrientacao = notas
    .filter((n) => n.orientacoes_casa && n.orientacoes_casa.trim())
    .sort((a, b) => b.data_sessao.localeCompare(a.data_sessao));

  const nFeitas = comOrientacao.filter((n) => n.orientacao_feita_em).length;

  const nomeProf = (id: string) =>
    profissionais.find((p) => p.id === id)?.nome ?? "profissional";
  const alimento = (id: string | null) =>
    id ? alimentos.find((a) => a.id === id) ?? null : null;

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>
      <h1 className="tela-titulo">pra fazer em casa</h1>
      <p className="tela-sub">
        {crianca ? `${crianca.apelido} · ` : ""}as orientações da equipe, reunidas. marque
        o que já fez em casa.
      </p>

      {comOrientacao.length > 0 && (
        <p className="meta" style={{ color: "var(--text-muted)", marginTop: "calc(var(--s2) * -1)", marginBottom: "var(--s4)" }}>
          {nFeitas} de {comOrientacao.length} {comOrientacao.length === 1 ? "feita" : "feitas"}
        </p>
      )}

      {comOrientacao.length === 0 ? (
        <div className="vazio">
          <span className="emoji" aria-hidden>
            🏠
          </span>
          ainda sem orientações para casa. elas aparecem aqui quando uma sessão registrar
          uma.
        </div>
      ) : (
        comOrientacao.map((n) => {
          const a = alimento(n.alimento_id);
          const feita = !!n.orientacao_feita_em;
          return (
            <div className={`card casa-item${feita ? " casa-feita" : ""}`} key={n.id}>
              <div className="row-entre" style={{ alignItems: "flex-start" }}>
                <span className="row" style={{ gap: "var(--s2)" }}>
                  <EspecialidadeBadge especialidade={n.especialidade} curto />
                  <span className="meta" style={{ color: "var(--text-muted)" }}>
                    {nomeProf(n.profissional_id)}
                  </span>
                </span>
                <span className="meta" style={{ color: "var(--text-suave)" }}>
                  {dataCurta(n.data_sessao)}
                </span>
              </div>
              <p className="casa-texto">
                {a && (
                  <span aria-hidden style={{ marginRight: 6 }}>
                    {emojiAlimento(a.nome, a.categoria)}
                  </span>
                )}
                {n.orientacoes_casa}
              </p>
              {(a || n.objetivo) && (
                <div className="meta" style={{ color: "var(--text-suave)" }}>
                  {a ? `${a.nome} · ` : ""}objetivo: {n.objetivo}
                </div>
              )}

              <button
                type="button"
                className={`casa-toggle${feita ? " feita" : ""}`}
                aria-pressed={feita}
                onClick={() => marcarOrientacaoFeita(n.id, !feita)}
              >
                <span className="casa-check" aria-hidden>
                  {feita ? "✓" : ""}
                </span>
                {feita
                  ? `feito em casa · ${dataCurta(n.orientacao_feita_em!)}`
                  : "marcar como feito em casa"}
              </button>
            </div>
          );
        })
      )}
    </main>
  );
}
